import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import LoginForm from './components/LoginForm';
import TrustIndicators from './components/TrustIndicators';
import GuestCheckoutOption from './components/GuestCheckoutOption';
import { useAuth } from '../../context/AuthContext';
import Footer from '../../components/ui/Footer';
import CustomerHeader from '../../components/ui/CustomerHeader';


const UserLogin = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // Redirect if user is already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      if (user?.role === 'admin') {
        navigate('/admin-dashboard');
      } else {
        navigate('/user-account-dashboard');
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Don't render login form if user is already authenticated
  if (isAuthenticated()) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <CustomerHeader
        cartItemCount={0}
        onCartClick={() => {}}
      />

      <div className="pt-20 pb-16 flex-1">
        <Helmet>
          <title>Sign In - Lalay Fashion | Access Your Beauty Account</title>
          <meta name="description" content="Sign in to your Lalay Fashion account to access your wishlist, order history, and exclusive beauty deals. Secure login with social options available." />
          <meta name="keywords" content="beauty login, skincare account, makeup signin, fashion items, cosmetics login" />
        </Helmet>
          {/* Main Content */}
          <main className="flex-1">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Left Column - Hero Content */}
                <div className="space-y-8">
                  <div className="text-center lg:text-left">
                    <h1 className="text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-4">
                      Welcome Back to Your
                      <span className="text-primary block">Style Journey</span>
                    </h1>
                    <p className="text-lg text-muted-foreground mb-8">
                      Sign in to access your personalized style picks, wishlist, and exclusive member benefits.
                    </p>
                  </div>

                  {/* Hero Image */}
                  <div className="relative rounded-2xl overflow-hidden shadow-elevation-2">
                    <Image
                      src="https://lalayfashion.com/wp-content/uploads/2026/05/153affad-590a-43ca-b71b-17596f65aa1c.jpg"
                      alt="Summer casual sleeveless vest belt midi dress styled for warm-weather everyday wear"
                      className="w-full h-64 lg:h-80 object-cover" />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <Icon name="Heart" size={24} className="text-primary mb-2" />
                      <h3 className="text-sm font-medium text-foreground mb-1">Wishlist</h3>
                      <p className="text-xs text-muted-foreground">Save your favorite products</p>
                    </div>
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <Icon name="Package" size={24} className="text-primary mb-2" />
                      <h3 className="text-sm font-medium text-foreground mb-1">Order History</h3>
                      <p className="text-xs text-muted-foreground">Track your purchases</p>
                    </div>
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <Icon name="Star" size={24} className="text-primary mb-2" />
                      <h3 className="text-sm font-medium text-foreground mb-1">Reviews</h3>
                      <p className="text-xs text-muted-foreground">Share your experiences</p>
                    </div>
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <Icon name="Gift" size={24} className="text-primary mb-2" />
                      <h3 className="text-sm font-medium text-foreground mb-1">Rewards</h3>
                      <p className="text-xs text-muted-foreground">Earn points & discounts</p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Login Form */}
                <div className="space-y-8">
                  <div className="bg-card rounded-2xl border border-border shadow-elevation-2 p-8">
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                        Sign In to Your Account
                      </h2>
                      <p className="text-muted-foreground">
                        Access your beauty profile and continue shopping
                      </p>
                    </div>

                    {/* Login Form */}
                    <LoginForm isLoading={false} externalLoading={false} />
                  </div>

                  {/* Trust Indicators */}
                  <TrustIndicators />

                  {/* Guest Checkout Option */}
                  <GuestCheckoutOption />
                </div>
              </div>
            </div>
          </main>

      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default UserLogin;